// src/utils/nativeApp.js
/**
 * Native App Integration Utility
 * Provides bridge between web app and native apps (Android/iOS) via WebView JavaScript interface
 *
 * The Android app exposes `YumExpressAndroid` interface for:
 * - FCM device token registration
 * - Push notification handling
 * - Device information
 *
 * The iOS app exposes `YumExpressiOS` interface (via WKWebView messageHandlers) for:
 * - APNs/FCM device token registration
 * - Push notification handling
 * - Device information
 */

/**
 * Check if running inside the native Android app
 * @returns {boolean}
 */
export const isAndroidApp = () => {
  return typeof window !== 'undefined' && typeof window.YumExpressAndroid !== 'undefined' && window.YumExpressAndroid.isNativeApp()
}

/**
 * Check if running inside the native iOS app
 * @returns {boolean}
 */
export const isiOSApp = () => {
  return typeof window !== 'undefined' && window.webkit?.messageHandlers?.YumExpressiOS !== undefined
}

/**
 * Check if running inside any native app (Android or iOS)
 * @returns {boolean}
 */
export const isNativeApp = () => {
  return isAndroidApp() || isiOSApp()
}

/**
 * Called after successful user login to register FCM device token
 * The native app will:
 * 1. Get the FCM token (waiting for APNs token if needed)
 * 2. Register it with the backend API
 *
 * On iOS the APNs token can arrive *after* login completes (especially the
 * first time the user grants notification permission, which can happen
 * during a brand-new Apple sign-in). We therefore re-post the message a few
 * times on iOS so the native side has multiple chances to act once its
 * `Messaging.messaging().apnsToken` is finally set. The native handler must
 * be idempotent (only POST `/notifications/devices/` once per token).
 *
 * @param {string} authToken - JWT access token for API authentication
 */
const IOS_LOGIN_RETRY_DELAYS_MS = [0, 1500, 4000, 9000] // ~14s total

const postiOSLogin = authToken => {
  try {
    window.webkit.messageHandlers.YumExpressiOS.postMessage({
      action: 'onUserLogin',
      authToken
    })
  } catch (error) {
    console.error('[NativeApp] Error calling onUserLogin on iOS:', error)
  }
}

export const onUserLogin = authToken => {
  if (!authToken) return
  if (isAndroidApp()) {
    try {
      window.YumExpressAndroid.onUserLogin(authToken)
    } catch (error) {
      console.error('[NativeApp] Error calling onUserLogin on Android:', error)
    }
  } else if (isiOSApp()) {
    // Fire immediately + retry to defeat the APNs/FCM token race that
    // sometimes makes Apple sign-in fail to register a device while
    // Google sign-in (which has already triggered notification prompts
    // earlier in the session) succeeds.
    IOS_LOGIN_RETRY_DELAYS_MS.forEach(delay => {
      if (delay === 0) postiOSLogin(authToken)
      else setTimeout(() => postiOSLogin(authToken), delay)
    })
  }
}

/**
 * Called when user logs out to unregister FCM device
 * The native app will unregister the device from push notifications
 */
export const onUserLogout = () => {
  if (isAndroidApp()) {
    try {
      window.YumExpressAndroid.onUserLogout()
    } catch (error) {
      console.error('[NativeApp] Error calling onUserLogout on Android:', error)
    }
  } else if (isiOSApp()) {
    try {
      window.webkit.messageHandlers.YumExpressiOS.postMessage({
        action: 'onUserLogout'
      })
    } catch (error) {
      console.error('[NativeApp] Error calling onUserLogout on iOS:', error)
    }
  }
}

/**
 * Get the FCM device token from native app
 * @returns {string|null} - FCM token or null if not available
 */
export const getFCMToken = () => {
  if (isAndroidApp()) {
    try {
      const token = window.YumExpressAndroid.getFCMToken()
      return token || null
    } catch (error) {
      console.error('[NativeApp] Error getting FCM token from Android:', error)
      return null
    }
  } else if (isiOSApp()) {
    // iOS returns token via callback, check if it's cached on window
    try {
      return window.YumExpressiOSToken || null
    } catch (error) {
      console.error('[NativeApp] Error getting FCM token from iOS:', error)
      return null
    }
  }
  return null
}

/**
 * Get device information from native app
 * @returns {Object|null} - Device info object or null
 * @example
 * {
 *   platform: "android",
 *   manufacturer: "Samsung",
 *   model: "SM-S901U",
 *   sdk: 33,
 *   version: "13"
 * }
 */
export const getDeviceInfo = () => {
  if (isAndroidApp()) {
    try {
      const info = window.YumExpressAndroid.getDeviceInfo()
      return info ? JSON.parse(info) : null
    } catch (error) {
      console.error('[NativeApp] Error getting device info from Android:', error)
      return null
    }
  } else if (isiOSApp()) {
    // iOS caches device info on window
    try {
      return window.YumExpressiOSDeviceInfo || null
    } catch (error) {
      console.error('[NativeApp] Error getting device info from iOS:', error)
      return null
    }
  }
  return null
}

/**
 * Get device type for API registration
 * @returns {'android'|'ios'|'web'}
 */
export const getDeviceType = () => {
  if (isAndroidApp()) {
    return 'android'
  }

  if (isiOSApp()) {
    return 'ios'
  }

  // Check for iOS browser (not in native app)
  const userAgent = navigator.userAgent || navigator.vendor || ''
  if (/iPad|iPhone|iPod/.test(userAgent)) {
    return 'ios'
  }

  return 'web'
}

/**
 * Get device name for API registration
 * @returns {string}
 */
export const getDeviceName = () => {
  if (isAndroidApp()) {
    const info = getDeviceInfo()
    if (info) {
      return `${info.manufacturer || 'Unknown'} ${info.model || 'Device'}`
    }
  }

  if (isiOSApp()) {
    const info = getDeviceInfo()
    if (info) {
      return info.model || 'iPhone'
    }
    return 'iPhone'
  }

  // Fallback to browser info
  const userAgent = navigator.userAgent || ''
  if (userAgent.includes('Chrome')) return 'Chrome Browser'
  if (userAgent.includes('Firefox')) return 'Firefox Browser'
  if (userAgent.includes('Safari')) return 'Safari Browser'
  if (userAgent.includes('Edge')) return 'Edge Browser'

  return 'Web Browser'
}

/**
 * Request notification permission (for web push notifications)
 * Native apps handle permissions separately
 * @returns {Promise<boolean>} - Whether permission was granted
 */
export const requestNotificationPermission = async () => {
  // Native app handles its own permissions
  if (isNativeApp()) {
    return true
  }

  // Web Push Notification permission
  if ('Notification' in window) {
    if (Notification.permission === 'granted') {
      return true
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission()
      return permission === 'granted'
    }
  }

  return false
}

/**
 * Check if push notifications are supported
 * @returns {boolean}
 */
export const isPushSupported = () => {
  if (isNativeApp()) {
    return true
  }

  return 'Notification' in window && 'serviceWorker' in navigator
}

/**
 * Show a local notification (web only, native handles its own)
 * @param {string} title - Notification title
 * @param {Object} options - Notification options
 */
export const showLocalNotification = (title, options = {}) => {
  if (isNativeApp()) {
    // Native app shows notifications via FCM
    return
  }

  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification(title, {
      icon: '/favicon.png',
      badge: '/favicon.png',
      ...options
    })
  }
}

// ==================== Live Order Tracking (Zomato-style) ====================

/**
 * Notify the native app that the user just placed a new order.
 *
 * On iOS, this triggers (via the `onOrderPlaced` action handled in
 * `YumExpressBridge.swift`):
 *   - `LiveTrackingManager.shared.showTracking(...)` → bottom Zomato-style banner.
 *   - `FCMManager.shared.startLiveActivity(...)` → Lock-Screen + Dynamic Island
 *     Live Activity (iOS 16.2+; silently no-ops on older versions).
 *
 * Subsequent backend `delivery_update` FCM pushes will then update the same
 * banner / Live Activity instead of creating duplicates.
 *
 * Android: best-effort — calls `YumExpressAndroid.onOrderPlaced(json)` if the
 * native side exposes it; safely ignored otherwise.
 *
 * The native handler is tolerant of camelCase or snake_case keys, so callers
 * may pass either. Required: `orderId`. Everything else is optional.
 *
 * @param {Object} info - Order info forwarded to the native bridge.
 * @param {string|number} info.orderId        - Required. Order identifier.
 * @param {string} [info.vendorName]          - Restaurant / vendor name shown in the banner header.
 * @param {number} [info.etaMinutes=30]       - Estimated delivery minutes.
 * @param {string} [info.orderTotal]          - Pre-formatted total (e.g. "Tsh 12,500"). Shown on Live Activity.
 * @param {string} [info.deliveryAddress]     - Delivery address shown on Live Activity.
 * @param {string} [info.status='Order received']  - Banner subtitle.
 * @param {string} [info.step='order_received']    - one of: order_received | preparing | ready | on_the_way | delivered.
 */
export const onOrderPlaced = info => {
  if (!info || (!info.orderId && !info.order_id)) {
    console.warn('[NativeApp] onOrderPlaced called without orderId; ignoring.')
    return
  }

  const payload = {
    action: 'onOrderPlaced',
    orderId: info.orderId ?? info.order_id,
    vendorName: info.vendorName ?? info.vendor_name ?? '',
    etaMinutes: info.etaMinutes ?? info.eta_minutes ?? 30,
    orderTotal: info.orderTotal ?? info.order_total ?? '',
    deliveryAddress: info.deliveryAddress ?? info.delivery_address ?? '',
    status: info.status ?? 'Order received',
    step: info.step ?? 'order_received'
  }

  if (isiOSApp()) {
    try {
      window.webkit.messageHandlers.YumExpressiOS.postMessage(payload)
    } catch (error) {
      console.error('[NativeApp] Error calling onOrderPlaced on iOS:', error)
    }
    return
  }

  if (isAndroidApp()) {
    try {
      // Best-effort: only call if the Android side exposes it.
      if (typeof window.YumExpressAndroid.onOrderPlaced === 'function') {
        window.YumExpressAndroid.onOrderPlaced(JSON.stringify(payload))
      }
    } catch (error) {
      console.error('[NativeApp] Error calling onOrderPlaced on Android:', error)
    }
  }
}

/**
 * Update a running iOS Live Activity (and Android tracking banner if the
 * shell supports it) with a fresh tracking snapshot.
 *
 * This is a **local** update path: the native shell calls
 * `Activity.update(...)` directly using values it just received from the
 * WebView. No backend / APNs Live Activity push token is required.
 *
 * Trade-off: when the app is killed the Live Activity will stop refreshing
 * and iOS will dim it after `staleDate`. That's the price of skipping the
 * APNs Live Activity backend integration.
 *
 * Called by `useOrderTracking` on every `delivery_tracking_update` /
 * `status_update` / `location_update` WebSocket message and on every
 * relevant native push the WebView receives while in foreground.
 *
 * Native handler MUST be idempotent and tolerant of partial payloads —
 * fields may be `null`/missing depending on which event fired.
 *
 * @param {Object} info
 * @param {string|number} info.orderId          - REQUIRED. Order identifier.
 * @param {string} [info.status]                - Order status (e.g. 'preparing', 'out_for_delivery').
 * @param {'pickup'|'delivery'|null} [info.phase]
 * @param {number|null} [info.etaMinutes]       - Best ETA we have right now.
 * @param {string} [info.vendorName]
 * @param {string} [info.deliveryAddress]
 * @param {string} [info.driverName]
 * @param {number|null} [info.driverLat]
 * @param {number|null} [info.driverLng]
 * @param {string} [info.statusMessage]         - Optional human-readable subtitle.
 */
export const updateLiveActivity = info => {
  if (!info || (!info.orderId && !info.order_id)) return

  const payload = {
    action: 'updateLiveActivity',
    orderId: info.orderId ?? info.order_id,
    status: info.status ?? null,
    phase: info.phase ?? null,
    etaMinutes: info.etaMinutes ?? info.eta_minutes ?? null,
    vendorName: info.vendorName ?? info.vendor_name ?? '',
    deliveryAddress: info.deliveryAddress ?? info.delivery_address ?? '',
    driverName: info.driverName ?? info.driver_name ?? '',
    driverLat: info.driverLat ?? info.driver_lat ?? null,
    driverLng: info.driverLng ?? info.driver_lng ?? null,
    statusMessage: info.statusMessage ?? info.status_message ?? ''
  }

  if (isiOSApp()) {
    try {
      window.webkit.messageHandlers.YumExpressiOS.postMessage(payload)
    } catch (error) {
      console.error('[NativeApp] Error calling updateLiveActivity on iOS:', error)
    }
    return
  }

  if (isAndroidApp()) {
    try {
      if (typeof window.YumExpressAndroid.updateLiveActivity === 'function') {
        window.YumExpressAndroid.updateLiveActivity(JSON.stringify(payload))
      }
    } catch (error) {
      console.error('[NativeApp] Error calling updateLiveActivity on Android:', error)
    }
  }
}

/**
 * End a running iOS Live Activity (and Android tracking banner if exposed).
 *
 * Called by `useOrderTracking` when the order reaches a terminal state
 * (`delivered`, `cancelled`, etc.). The native handler should call
 * `Activity.end(..., dismissalPolicy: .default)` and ensure the final
 * `ContentState` reflects the actual terminal status — i.e. don't show
 * "Delivered! 🎉" when `terminalStatus === 'cancelled'`.
 *
 * @param {Object} info
 * @param {string|number} info.orderId
 * @param {'delivered'|'cancelled'|string} info.terminalStatus
 */
export const endLiveActivity = info => {
  if (!info || (!info.orderId && !info.order_id)) return

  const payload = {
    action: 'endLiveActivity',
    orderId: info.orderId ?? info.order_id,
    terminalStatus: info.terminalStatus ?? info.terminal_status ?? 'delivered'
  }

  if (isiOSApp()) {
    try {
      window.webkit.messageHandlers.YumExpressiOS.postMessage(payload)
    } catch (error) {
      console.error('[NativeApp] Error calling endLiveActivity on iOS:', error)
    }
    return
  }

  if (isAndroidApp()) {
    try {
      if (typeof window.YumExpressAndroid.endLiveActivity === 'function') {
        window.YumExpressAndroid.endLiveActivity(JSON.stringify(payload))
      }
    } catch (error) {
      console.error('[NativeApp] Error calling endLiveActivity on Android:', error)
    }
  }
}

// ==================== In-App Review Methods ====================

/**
 * Call when order is completed (tracks for review prompt timing)
 * This helps the native app track when to show the review prompt
 */
export const onOrderCompleted = () => {
  if (isAndroidApp()) {
    try {
      window.YumExpressAndroid.onOrderCompleted()
    } catch (error) {
      console.error('[NativeApp] Error calling onOrderCompleted:', error)
    }
  } else if (isiOSApp()) {
    try {
      window.webkit.messageHandlers.YumExpressiOS.postMessage({
        action: 'onOrderCompleted'
      })
    } catch (error) {
      console.error('[NativeApp] Error calling onOrderCompleted on iOS:', error)
    }
  }
}

/**
 * Request app review (shows Google Play In-App Review or iOS App Store Review dialog)
 * This will show the native review dialog
 */
export const requestReview = () => {
  if (isAndroidApp()) {
    try {
      window.YumExpressAndroid.requestReview()
    } catch (error) {
      console.error('[NativeApp] Error calling requestReview:', error)
    }
  } else if (isiOSApp()) {
    try {
      window.webkit.messageHandlers.YumExpressiOS.postMessage({
        action: 'requestReview'
      })
    } catch (error) {
      console.error('[NativeApp] Error calling requestReview on iOS:', error)
    }
  }
}

/**
 * Request review only if eligibility conditions are met
 * Conditions: 3+ app launches, 1+ orders, 2+ days since install, 30+ days since last prompt
 */
export const requestReviewIfEligible = () => {
  if (isAndroidApp()) {
    try {
      window.YumExpressAndroid.requestReviewIfEligible()
    } catch (error) {
      console.error('[NativeApp] Error calling requestReviewIfEligible:', error)
    }
  } else if (isiOSApp()) {
    try {
      window.webkit.messageHandlers.YumExpressiOS.postMessage({
        action: 'requestReviewIfEligible'
      })
    } catch (error) {
      console.error('[NativeApp] Error calling requestReviewIfEligible on iOS:', error)
    }
  }
}

/**
 * Open App Store page directly
 * Use this as a fallback or for "Rate Us" buttons
 */
export const openAppStore = () => {
  if (isAndroidApp()) {
    try {
      window.YumExpressAndroid.openPlayStore()
    } catch (error) {
      console.error('[NativeApp] Error calling openPlayStore:', error)
    }
  } else if (isiOSApp()) {
    try {
      window.webkit.messageHandlers.YumExpressiOS.postMessage({
        action: 'openAppStore'
      })
    } catch (error) {
      console.error('[NativeApp] Error calling openAppStore on iOS:', error)
    }
  }
}

/**
 * Open Play Store page directly (Android only, alias for backward compatibility)
 * Use openAppStore() for cross-platform support
 */
export const openPlayStore = () => {
  if (isAndroidApp()) {
    try {
      window.YumExpressAndroid.openPlayStore()
    } catch (error) {
      console.error('[NativeApp] Error calling openPlayStore:', error)
    }
  }
}

/**
 * Set "never ask for review again" preference
 * @param {boolean} neverAsk - If true, never prompt for review again
 */
export const setNeverAskReview = neverAsk => {
  if (isAndroidApp()) {
    try {
      window.YumExpressAndroid.setNeverAskReview(neverAsk)
    } catch (error) {
      console.error('[NativeApp] Error calling setNeverAskReview:', error)
    }
  } else if (isiOSApp()) {
    try {
      window.webkit.messageHandlers.YumExpressiOS.postMessage({
        action: 'setNeverAskReview',
        neverAsk: neverAsk
      })
    } catch (error) {
      console.error('[NativeApp] Error calling setNeverAskReview on iOS:', error)
    }
  }
}

// ==================== Native Review Dialog Methods ====================

/**
 * Show custom native review dialog with star rating
 * This shows a beautiful native dialog with:
 * - Star rating bar (1-5 stars)
 * - "Rate on App Store" button (for 4-5 stars)
 * - "Send Feedback" button (for 1-3 stars)
 * - "Maybe Later" and "Don't ask again" options
 */
export const showReviewDialog = () => {
  if (isAndroidApp()) {
    try {
      window.YumExpressAndroid.showReviewDialog()
    } catch (error) {
      console.error('[NativeApp] Error calling showReviewDialog:', error)
    }
  } else if (isiOSApp()) {
    try {
      window.webkit.messageHandlers.YumExpressiOS.postMessage({
        action: 'showReviewDialog'
      })
    } catch (error) {
      console.error('[NativeApp] Error calling showReviewDialog on iOS:', error)
    }
  }
}

/**
 * Show review dialog only if eligible (respects timing and preferences)
 * Best to call after successful order delivery
 */
export const showReviewDialogIfEligible = () => {
  if (isAndroidApp()) {
    try {
      window.YumExpressAndroid.showReviewDialogIfEligible()
    } catch (error) {
      console.error('[NativeApp] Error calling showReviewDialogIfEligible:', error)
    }
  } else if (isiOSApp()) {
    try {
      window.webkit.messageHandlers.YumExpressiOS.postMessage({
        action: 'showReviewDialogIfEligible'
      })
    } catch (error) {
      console.error('[NativeApp] Error calling showReviewDialogIfEligible on iOS:', error)
    }
  }
}

// ==================== Native Social Sign-In Methods ====================

/**
 * Ask the native app to run Google Sign-In (Credential Manager on Android,
 * GoogleSignIn SDK on iOS) and return an ID token.
 *
 * Native contract:
 *   Android: window.YumExpressAndroid.requestGoogleSignIn(requestId)
 *   iOS:     postMessage({ action: 'requestGoogleSignIn', requestId })
 *   Both MUST call window.__yumSocialAuthResolve(requestId, { idToken }) on success
 *   or window.__yumSocialAuthReject(requestId, 'message') on failure.
 *
 * @returns {Promise<{ idToken: string }>}
 */
export const requestNativeGoogleSignIn = () => {
  return new Promise((resolve, reject) => {
    if (!isNativeApp()) return reject(new Error('Not running in a native app'))
    const requestId = `google_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
    registerSocialAuthResolver(requestId, resolve, reject)

    if (isAndroidApp()) {
      try {
        if (typeof window.YumExpressAndroid.requestGoogleSignIn === 'function') {
          window.YumExpressAndroid.requestGoogleSignIn(requestId)
        } else {
          reject(new Error('Android bridge does not implement requestGoogleSignIn'))
        }
      } catch (e) {
        reject(e)
      }
    } else if (isiOSApp()) {
      try {
        window.webkit.messageHandlers.YumExpressiOS.postMessage({
          action: 'requestGoogleSignIn',
          requestId
        })
      } catch (e) {
        reject(e)
      }
    }
  })
}

/**
 * Ask the native app to run Apple Sign-In (ASAuthorizationAppleIDRequest on iOS).
 * Android is not supported — Apple Sign-In is iOS-only in native.
 *
 * Native contract:
 *   iOS: postMessage({ action: 'requestAppleSignIn', requestId })
 *   iOS MUST call window.__yumSocialAuthResolve(requestId, {
 *     idToken, fullName?, email?
 *   }) or window.__yumSocialAuthReject(requestId, 'message').
 *
 * @returns {Promise<{ idToken: string, fullName?: string, email?: string }>}
 */
export const requestNativeAppleSignIn = () => {
  return new Promise((resolve, reject) => {
    if (!isiOSApp()) return reject(new Error('Apple Sign-In is only available on iOS'))
    const requestId = `apple_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
    registerSocialAuthResolver(requestId, resolve, reject)
    try {
      window.webkit.messageHandlers.YumExpressiOS.postMessage({
        action: 'requestAppleSignIn',
        requestId
      })
    } catch (e) {
      reject(e)
    }
  })
}

// Resolver registry — native code calls window.__yumSocialAuthResolve / Reject
const _socialAuthPromises = new Map()
const SOCIAL_AUTH_TIMEOUT_MS = 120000

const registerSocialAuthResolver = (id, resolve, reject) => {
  const timer = setTimeout(() => {
    _socialAuthPromises.delete(id)
    reject(new Error('Native sign-in timed out'))
  }, SOCIAL_AUTH_TIMEOUT_MS)
  _socialAuthPromises.set(id, { resolve, reject, timer })
}

// ==================== Native High-Accuracy Location ====================

/**
 * Ask the native app for the user's current high-accuracy location using
 * the platform's native API (CoreLocation on iOS, FusedLocationProvider on
 * Android). This is preferred over `navigator.geolocation` inside a WebView
 * because:
 *   - iOS WKWebView routinely rejects `getCurrentPosition` unless explicit
 *     `NSLocationWhenInUseUsageDescription` + JS-bridged permission flow.
 *   - Android System WebView passes the request to the host app which often
 *     drops it silently when no `WebChromeClient.onGeolocationPermissions…`
 *     handler is wired.
 *   - Native APIs return a fix in ~300 ms with sub-10 m accuracy; the
 *     WebView geolocation fallback can take 5–15 s with 50–500 m accuracy.
 *
 * Native contract:
 *   Android: window.YumExpressAndroid.requestCurrentLocation(requestId)
 *   iOS:     postMessage({ action: 'requestCurrentLocation', requestId })
 *   Both MUST call:
 *     window.__yumLocationResolve(requestId, { lat, lng, accuracy?, heading? })
 *   on success, or
 *     window.__yumLocationReject(requestId, 'permission_denied' | 'timeout' | 'unavailable')
 *   on failure.
 *
 * @returns {Promise<{ lat:number, lng:number, accuracy?:number, heading?:number }>}
 */
const _locationPromises = new Map()
const NATIVE_LOCATION_TIMEOUT_MS = 15000

const registerLocationResolver = (id, resolve, reject) => {
  const timer = setTimeout(() => {
    _locationPromises.delete(id)
    reject(new Error('Native location timed out'))
  }, NATIVE_LOCATION_TIMEOUT_MS)
  _locationPromises.set(id, { resolve, reject, timer })
}

export const requestNativeCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    if (!isNativeApp()) return reject(new Error('Not running in a native app'))
    const requestId = `loc_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
    registerLocationResolver(requestId, resolve, reject)

    if (isAndroidApp()) {
      try {
        if (typeof window.YumExpressAndroid.requestCurrentLocation === 'function') {
          window.YumExpressAndroid.requestCurrentLocation(requestId)
        } else {
          // Older native build — fall back to web geolocation by rejecting
          // with a sentinel the caller can detect.
          reject(new Error('not_implemented'))
        }
      } catch (e) {
        reject(e)
      }
    } else if (isiOSApp()) {
      try {
        window.webkit.messageHandlers.YumExpressiOS.postMessage({
          action: 'requestCurrentLocation',
          requestId
        })
      } catch (e) {
        reject(e)
      }
    }
  })
}

if (typeof window !== 'undefined') {
  window.__yumLocationResolve = (id, payload) => {
    const entry = _locationPromises.get(id)
    if (!entry) return
    clearTimeout(entry.timer)
    _locationPromises.delete(id)
    entry.resolve(payload || {})
  }
  window.__yumLocationReject = (id, reason) => {
    const entry = _locationPromises.get(id)
    if (!entry) return
    clearTimeout(entry.timer)
    _locationPromises.delete(id)
    entry.reject(new Error(reason || 'native_location_failed'))
  }
}

/**
 * Cross-platform: try native first (sub-second, high accuracy), fall back
 * to `navigator.geolocation` on the web or if the native bridge isn't
 * implemented yet. Resolves with `{ lat, lng, accuracy?, source }`.
 *
 * `source` is one of: 'native' | 'web'.
 */
export const getCurrentLocation = async (opts = {}) => {
  const { timeout = 12000, enableHighAccuracy = true } = opts

  if (isNativeApp()) {
    try {
      const native = await requestNativeCurrentLocation()
      if (native?.lat != null && native?.lng != null) {
        return { ...native, source: 'native' }
      }
    } catch (e) {
      // Fall through to web geolocation only if native didn't outright deny
      if (e?.message === 'permission_denied') throw e
    }
  }

  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    throw new Error('geolocation_unsupported')
  }
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      pos =>
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          heading: pos.coords.heading || undefined,
          source: 'web'
        }),
      err => reject(err),
      { enableHighAccuracy, timeout, maximumAge: 30000 }
    )
  })
}

if (typeof window !== 'undefined') {
  window.__yumSocialAuthResolve = (id, payload) => {
    const entry = _socialAuthPromises.get(id)
    if (!entry) return
    clearTimeout(entry.timer)
    _socialAuthPromises.delete(id)
    entry.resolve(payload || {})
  }
  window.__yumSocialAuthReject = (id, message) => {
    const entry = _socialAuthPromises.get(id)
    if (!entry) return
    clearTimeout(entry.timer)
    _socialAuthPromises.delete(id)
    entry.reject(new Error(message || 'Native sign-in failed'))
  }
}

// Default export with all methods
const NativeApp = {
  isNative: isNativeApp,
  isAndroid: isAndroidApp,
  isiOS: isiOSApp,
  onLogin: onUserLogin,
  onLogout: onUserLogout,
  getFCMToken,
  getDeviceInfo,
  getDeviceType,
  getDeviceName,
  requestNotificationPermission,
  isPushSupported,
  showLocalNotification,
  // Live Order Tracking (Zomato-style banner + Live Activity)
  onOrderPlaced,
  updateLiveActivity,
  endLiveActivity,
  // In-App Review Methods
  onOrderCompleted,
  requestReview,
  requestReviewIfEligible,
  openAppStore,
  openPlayStore,
  setNeverAskReview,
  // Native Review Dialog Methods
  showReviewDialog,
  showReviewDialogIfEligible,
  // Native Social Sign-In
  requestNativeGoogleSignIn,
  requestNativeAppleSignIn,
  // Native High-Accuracy Location
  requestNativeCurrentLocation,
  getCurrentLocation
}

export default NativeApp
